/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Select as NextUiSelect,
  SelectItem,
  SelectSection,
} from "@nextui-org/select";
import styles from "./InputSelect.module.scss";
import { useState, useEffect, useRef, Key, ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";
import Button from "../Button/Button";
import ControlledRadio from "../ControlledRadio/ControlledRadio";
import {
  selectChangeHandler,
  getRenderValue,
  valueIsSelected,
} from "./utils/index";
import { SelectProps, Tooltip } from "@nextui-org/react";
import DatePicker from "../DatePicker/DatePicker";
import { DateRange } from "../DatePicker/DatePicker";
import Checkbox from "../Checkbox/Checkbox";
//Icons
import InvalidIcon from "../../assets/images/icons/ui/states/alert-icon.svg";
import ChevronIcon from "../../assets/images/icons/ui/arrows/chevron-icon.svg";
import CalendarIcon from "../../assets/images/icons/calendar-icon.svg";
import CloseIcon from "../../assets/images/icons/close-icon.svg";
import InfoIcon from "../../assets/images/icons/ui/states/alert-light-blue-icon.svg";

export type Values = string[] | string;

export interface Item {
  value: string;
  label: string;
}

export interface SelectItemType extends Item {
  options?: Item[];
  downDivider?: boolean;
  selectAll?: boolean;
}

export interface SelectWithOption extends SelectItemType {
  options: Item[];
}

export type SelectType =
  | "single"
  | "multiple"
  | "single-radio"
  | "multiple-checkbox"
  | "multiple-sections-checkbox"
  | "single-radio-date-picker"
  | "single-radio-date-picker-day"
  | "single-radio-date-picker-without-month";

export interface NextUISelectProps extends Omit<
  SelectProps<SelectItemType>,
  | "labelPlacement"
  | "children"
  | "selectionMode"
  | "items"
  | "onSelectionChange"
  | "onChange"
  | "defaultSelectedKeys"
  | "errorMessage"
> {
  confirmSelection?: boolean;
  isFormField?: boolean;
  items: SelectItemType[];
  type: SelectType;
  onChange: (keys: string | string[]) => void;
  defaultSelectedKeys?: Values;
  touched?: boolean;
  errorMessage?: ReactNode;
}

export interface InputSelectProps extends NextUISelectProps {
  locale?: string;
  onDatepickerDateChanged?: (range: DateRange) => void;
  initialDatePickerRange?: DateRange;
  minDatePickerDate?: Date;
  maxDatePickerDate?: Date;
  inputValue?: Values;
  showExternalBox?: boolean;
  disabledOptions?: string[];
  optionsTooltip?: Record<string, JSX.Element> | undefined;
  getCustomSelectItem?: (item: SelectItemType) => React.ReactNode;
  removeIcon?: boolean;
  resetActionLabel?: boolean;
  minDays?: number;
  onReset?: () => void;
  calendarPreFooter?: ReactNode;
  calendarLimitMonthsAvailable?: boolean;
}

const InputSelect = ({
  placeholder,
  className,
  isInvalid,
  type,
  items,
  confirmSelection,
  isFormField,
  onChange,
  defaultSelectedKeys,
  description,
  touched,
  errorMessage,
  classNames,
  locale = "es-AR",
  onDatepickerDateChanged,
  onBlur,
  isDisabled,
  initialDatePickerRange,
  minDatePickerDate,
  maxDatePickerDate,
  inputValue,
  showExternalBox,
  label: labelComponent,
  onClose,
  disabledOptions,
  optionsTooltip,
  getCustomSelectItem,
  removeIcon = false,
  resetActionLabel,
  minDays,
  onReset,
  calendarPreFooter,
  calendarLimitMonthsAvailable = false,

  ...rest
}: InputSelectProps) => {
  const {
    containerSelect,
    invalidSelect,
    iconWrapper,
    labelCheckbox,
    confirmActions,
    confirmActionsListbox,
    confirmActionsListboxWrapper,
    listbox,
    datePickerListboxWrapper,
    itemDivider,
    section,
    checkOrRadio,
    label,
    formField,
    labelFix,
    error,
    icon,
    base,
    mainWrapper,
    trigger,
    selectorIcon,
    innerWrapper,
    valueStyle,
    popoverContent,
    datePickerPopoverContent,
    errorMessageStyle,
    descriptionStyle,
    helperWrapper,
    datePicker,
    datePickerOption,
    disabledSelect,
    externalBox,
    externalItem,
    optionsContainer,
    tooltipContainer,
    tooltipIcon,
    scrollableContent,
    footerClassName,
    ...restStyles
  } = styles;

  // Useful booleans to check type
  const hasCheckbox = type.includes("checkbox");
  const hasRadio = type.includes("radio");
  const isSingle = type.includes("single");
  const isMultiple = type.includes("multiple");
  const hasDatePicker = type.includes("date-picker");
  const isDateVariant = type.includes("day");
  const hideMonthlyOption = type.includes("without-month");

  // Id to difference component instances
  const [componentId, setComponentId] = useState<string>();

  // Handle open and close of select
  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);

  // Select values
  const [values, setValues] = useState<Values>(defaultSelectedKeys || []);

  // To set when input is touched
  const [selectTouched, setSelectTouched] = useState(touched || false);

  // To show a date picker and hide all options
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(isDateVariant);

  // Last date range selected in the date picker
  const [datePickerRange, setDatePickerRange] = useState<DateRange | undefined>(
    initialDatePickerRange,
  );

  // Date picker calendar variant shown intially when date picker was last opened
  const [datePickerInitialVariant, setDatePickerInitialVariant] = useState<
    "DAY" | "DATE" | "MONTH"
  >(isDateVariant ? "DATE" : "DAY");

  const [previousValues, setPreviousValues] = useState<Values>(values);
  const [confirmApplyChanges, setConfirmApplyChanges] = useState(false);

  const inputSelectRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const prevValuesBeforePicker = useRef<Values | null>(null);
  const prevRangeBeforePicker = useRef<DateRange | undefined>(undefined);

  useEffect(() => {
    if (!isOpen && isMultiple && !confirmApplyChanges) {
      setValues(
        previousValues?.length
          ? previousValues
          : defaultSelectedKeys || ["todos"],
      );
    } else {
      setConfirmApplyChanges(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (touched !== undefined) {
      setSelectTouched(touched);
    }
  }, [touched]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!popoverRef.current || !inputSelectRef.current) return;

    const inputSelectWidth = inputSelectRef.current.offsetWidth;

    if (isDatePickerOpen) {
      const datePickerWidth = 380;
      const currentLeft = parseFloat(popoverRef.current.style.left) || 0;
      popoverRef.current.dataset.originalLeft = String(currentLeft);
      const rightEdge = currentLeft + inputSelectWidth;
      popoverRef.current.style.width = `${datePickerWidth}px`;
      popoverRef.current.style.left = `${Math.max(0, rightEdge - datePickerWidth)}px`;
    } else {
      const originalLeft = popoverRef.current.dataset.originalLeft;
      if (originalLeft !== undefined) {
        popoverRef.current.style.left = `${originalLeft}px`;
        delete popoverRef.current.dataset.originalLeft;
      }
      popoverRef.current.style.width = `${inputSelectWidth}px`;
    }
  }, [isOpen, isDatePickerOpen]);

  // Listener for closing the select when dropdown is open and user clicks on modal close button
  // The popover renders on top of the close button, so we detect it via elementsFromPoint
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!isOpenRef.current) return;

      // When dropdown is open, check if user is clicking on the close button area
      const elementsAtPoint = document.elementsFromPoint(
        event.clientX,
        event.clientY,
      );

      const closeButtonEl = elementsAtPoint.find(
        (el) =>
          (el as HTMLElement).closest?.('button[aria-label="Close"]') !== null,
      );

      // If close button is detected while dropdown is open, close dropdown and notify modal
      if (closeButtonEl) {
        event.stopImmediatePropagation();
        event.preventDefault();
        setIsOpen(false);

        // Dispatch custom event for modal to handle the close
        const closeEvent = new CustomEvent("closeModalBehindPopover", {
          bubbles: true,
          cancelable: false,
          detail: { x: event.clientX, y: event.clientY },
        });
        document.dispatchEvent(closeEvent);
        return;
      }
    };

    // Handle click for normal outside click behavior
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isModalCloseButton =
        target.closest('button[aria-label="Close"]') !== null;

      if (isModalCloseButton && isOpenRef.current) {
        setIsOpen(false);
        return;
      }

      handleOutsideClick(event);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, [componentId, defaultSelectedKeys]);

  // Set the id to identify the component
  // This is done here for it to execute on the client and dont have discrepancies with the server
  useEffect(() => {
    setComponentId(uuidv4());
  }, []);

  // Change the values when the values are confirmed
  const handleConfirmChanges = () => {
    setConfirmApplyChanges(true);
    setPreviousValues(values);
    onChange(values);
    setIsOpen(false);
  };

  // Reset changes
  const handleResetChanges = () => {
    setConfirmApplyChanges(true);
    setPreviousValues(defaultSelectedKeys || []);
    setValues(defaultSelectedKeys || []);
    onChange(defaultSelectedKeys || []);
    setIsOpen(false);
  };

  // Handle click for multi section checkbox
  const handleSectionClick = (options: Item[], value: string) => {
    if (hasCheckbox) {
      let newSelectedValues: string[] = [];
      // Manage the parent and child values to set
      setValues((prev) => {
        const currentValues = prev ? Array.from(prev) : [];
        const optionValues = options.map((op) => op.value);
        const parentChildValues = [...optionValues, value];

        if (currentValues.includes(value)) {
          // If the current value is already selected, remove it and its children
          newSelectedValues = currentValues.filter(
            (val) => !parentChildValues.includes(val),
          );
        } else {
          // If the current value is not selected, add it along with its children
          newSelectedValues = [...currentValues, ...parentChildValues];
        }
        // Unique values
        return [...new Set(newSelectedValues)];
      });

      if (!confirmSelection) {
        onChange(newSelectedValues.filter((val) => !!val));
      }
    }
  };

  // Handle apply button click on the date picker. Close the picker and select
  // menu, set appropiate states and set select value to "date", which tells
  // the component to render some special value based on the selected range
  // (i.e. "May", "January - June", etc.)
  const onDatePickerApply = (range: DateRange) => {
    prevValuesBeforePicker.current = null;
    prevRangeBeforePicker.current = undefined;
    setDatePickerRange(range);
    const valueToSet = hideMonthlyOption ? "personalized" : "date";
    setValues(valueToSet);
    setIsDatePickerOpen(false);
    setIsOpen(false);
    if (onDatepickerDateChanged) onDatepickerDateChanged(range);
  };

  const onDatePickerDelete = () => {
    if (defaultSelectedKeys) {
      setValues(defaultSelectedKeys);
      onChange(defaultSelectedKeys);
    }
  };

  // Handle opening the date picker
  const openDatePicker = (variant: "DAY" | "MONTH") => {
    prevValuesBeforePicker.current = values;
    prevRangeBeforePicker.current = datePickerRange;
    setDatePickerInitialVariant(variant);
    setIsDatePickerOpen(true);
    if (hideMonthlyOption) setValues("personalized");
  };

  // Render Checkbox, Radio or label
  const ItemContent = ({ value, label }: Item) => {
    if (hasRadio) {
      return (
        <ControlledRadio
          label={label}
          value={value}
          name={label}
          checked={valueIsSelected(value, values)}
        />
      );
    } else if (hasCheckbox) {
      return (
        <>
          <Checkbox
            value={value}
            isSelected={valueIsSelected(value, values)}
          ></Checkbox>
          <span>{label}</span>
        </>
      );
    } else {
      return label;
    }
  };

  const getErrorMessage = () => (
    <div className={error}>
      <img src={InvalidIcon} className={icon} />
      {errorMessage ?? "Valor inválido"}
    </div>
  );

  // Listen to the "select-open" event and close input when the componentId is different
  useEffect(() => {
    const handleOtherSelectOpen = (event: Event) => {
      const [eventDetail, isDatePickerComponent] = (
        event as CustomEvent<string>
      ).detail.split(":");

      const isDatePicker = isDatePickerComponent.toLowerCase() === "true";

      if (eventDetail !== componentId && !isDatePicker) {
        setIsOpen(false);
      }
    };

    window.addEventListener("select-open", handleOtherSelectOpen);
    return () => {
      window.removeEventListener("select-open", handleOtherSelectOpen);
    };
  }, [componentId]);

  // Handler for inside clicks
  const handleInsideClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (popoverRef.current?.contains(target)) {
      return;
    }

    event.stopPropagation();

    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);

    if (hasDatePicker) {
      const hasSelectedCustomRange =
        (values === "personalized" || values === "date") &&
        datePickerRange !== undefined;
      const shouldOpenDatePicker = isDateVariant || hasSelectedCustomRange;

      setIsDatePickerOpen(shouldOpenDatePicker);

      if (shouldOpenDatePicker) {
        prevValuesBeforePicker.current = values;
        prevRangeBeforePicker.current = datePickerRange;
        if (hideMonthlyOption) setValues("personalized");
      }
    }

    const isDatePickerComponent =
      rest["aria-label"] === "Year" || rest["aria-label"] === "Month";

    const selectOpenEvent = new CustomEvent("select-open", {
      detail: `${componentId}:${isDatePickerComponent}`,
    });
    window.dispatchEvent(selectOpenEvent);
  };

  // Handler for outside clicks
  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (!target.isConnected) return;

    const selectOpen = isOpenRef.current;

    if (componentId && target) {
      const triggerElement = document.querySelector(
        `[data-id="${componentId}"]`,
      );
      const targetDataSlot = target.getAttribute("data-slot") || "";
      const noCloseSlots = ["listbox", "heading", "base"];
      // If select is open
      if (selectOpen) {
        // Check if the click is outside the select
        if (
          (triggerElement && triggerElement.contains(target)) ||
          noCloseSlots.includes(targetDataSlot) ||
          target.closest('[data-slot="popover"]') !== null ||
          (popoverRef.current && popoverRef.current.contains(target))
        ) {
          return; // Do nothing if it is a valid internal click
        }

        // Close the InputSelect in other cases
        setIsOpen(false);
      }
    }
  };

  useEffect(() => {
    if (inputValue !== undefined && !isDatePickerOpen) {
      setValues(inputValue);
    }
  }, [inputValue]);

  useEffect(() => {
    if (!isOpen && prevValuesBeforePicker.current !== null) {
      setValues(prevValuesBeforePicker.current);
      setDatePickerRange(prevRangeBeforePicker.current);
      prevValuesBeforePicker.current = null;
      prevRangeBeforePicker.current = undefined;
      setIsDatePickerOpen(false);
    }
  }, [isOpen]);

  // Listener for scroll events and close the select when the user scrolls
  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;

      // Si el target no es un HTMLElement, salimos
      if (!(target instanceof HTMLElement)) return;

      // Check if the scroll event is from the internal InputSelects
      const isInternalScroll =
        target.getAttribute("aria-label") === "Month" ||
        target.getAttribute("aria-label") === "Year" ||
        target.getAttribute("data-slot") === "popover" ||
        target.getAttribute("data-slot") === "listboxWrapper";

      if (
        inputSelectRef.current &&
        popoverRef.current &&
        !inputSelectRef.current.contains(event.target as Node) &&
        !popoverRef.current.contains(event.target as Node) &&
        !isInternalScroll
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  // On blur handler

  const initialBlurState = {
    triggered: false,
    event: null,
  };

  const [blur, setBlur] = useState<{
    triggered: boolean;
    event: React.FocusEvent | null;
  }>(initialBlurState);

  // Check if the blur event was triggered and the select is closed
  // If so, call the onBlur function and set touched to true
  // Reset the blur state
  useEffect(() => {
    if (blur.triggered && blur.event && !isOpen) {
      onBlur && onBlur(blur.event);
      setSelectTouched(true);
      setBlur(initialBlurState);
    }
  }, [isOpen]);

  const disabledAplyChanges = Array.isArray(values)
    ? values.length === 0 || (values.length === 1 && values[0] === "")
    : !values || values === "";

  return (
    <div
      id={`containerSelect-${componentId}`}
      ref={inputSelectRef}
      className={containerSelect}
      aria-label={rest["aria-label"]}
    >
      <NextUiSelect
        {...rest}
        isDisabled={isDisabled}
        disabled={isDisabled}
        disabledKeys={disabledOptions}
        disableAnimation={isDatePickerOpen}
        onClick={handleInsideClick}
        label={labelComponent}
        onBlur={(e: React.FocusEvent) => {
          // To avoid triggering onBlur when no option is selected
          setBlur({
            triggered: true,
            event: e,
          });
        }}
        popoverProps={{
          ref: popoverRef,
          ...(isDatePickerOpen && {
            classNames: {
              base: "!overflow-visible",
            },
          }),
        }}
        disallowEmptySelection={isSingle && true}
        description={!isInvalid && description}
        errorMessage={isInvalid && selectTouched && <>{getErrorMessage()}</>}
        selectionMode={isSingle || hasDatePicker ? "single" : "multiple"}
        scrollShadowProps={{
          isEnabled: false,
        }}
        data-id={componentId}
        items={items}
        placeholder={!inputValue ? (placeholder ?? " ") : " "}
        className={`${className} ${
          isInvalid && selectTouched ? invalidSelect : ""
        } ${isDisabled ? disabledSelect : ""}`}
        classNames={{
          base: `${base} ${classNames?.base}`,
          label: `${label} ${
            (isInvalid && selectTouched) || description ? labelFix : ""
          } ${isFormField && formField} ${isDisabled ? disabledSelect : ""} ${classNames?.label ?? ""}`,
          mainWrapper: `${mainWrapper} ${classNames?.mainWrapper}`,
          trigger: `${trigger} ${classNames?.trigger}`,
          innerWrapper: `${innerWrapper} ${classNames?.innerWrapper}`,
          selectorIcon: `${selectorIcon} ${classNames?.selectorIcon}`,
          value: `${valueStyle} ${classNames?.value}`,
          listboxWrapper: `${
            confirmSelection
              ? confirmActionsListboxWrapper
              : restStyles.listboxWrapper
          } ${isDatePickerOpen && datePickerListboxWrapper} ${
            classNames?.listboxWrapper
          }`,
          listbox: `${listbox} ${
            confirmSelection ? confirmActionsListbox : ""
          } ${isDatePickerOpen ? "overflow-visible" : ""} ${classNames?.listbox}`,
          popoverContent: `${popoverContent} ${isDatePickerOpen ? datePickerPopoverContent : ""} ${classNames?.popoverContent ?? ""}`,
          helperWrapper: `${helperWrapper} ${classNames?.helperWrapper}`,
          description: `${descriptionStyle} ${classNames?.description}`,
          errorMessage: `${errorMessageStyle} ${classNames?.errorMessage}`,
        }}
        isOpen={isOpen}
        isInvalid={isInvalid}
        selectedKeys={
          typeof values === "string" && isSingle ? [values] : values
        }
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          selectChangeHandler(
            e,
            items,
            type,
            values,
            setValues,
            setIsOpen,
            confirmSelection,
            setDatePickerRange,
          )
        }
        onSelectionChange={(keys: Iterable<Key>) => {
          if (!confirmSelection) {
            const keysArray = Array.from(keys).filter(Boolean) as Key[];
            if ((!keysArray || !keysArray[0]) && !isMultiple) return;
            onChange(isSingle ? String(keysArray[0]) : keysArray.map(String));
          }
        }}
        startContent={
          hasDatePicker && !removeIcon && <img src={CalendarIcon} />
        }
        renderValue={() =>
          getRenderValue(values, items, hasDatePicker, datePickerRange)
        }
        onClose={() => onClose && onClose()}
      >
        {/* Siempre renderizar items date/personalized para que el valor se pueda mostrar */}
        {items
          .filter(
            (item) => item.value === "date" || item.value === "personalized",
          )
          .map((item) => (
            <SelectItem
              key={item.value}
              value={item.value}
              aria-label={item.label}
              className="hidden"
              textValue={item.value}
            >
              {item.label}
            </SelectItem>
          ))}

        {!isDatePickerOpen &&
          items.map((item: SelectItemType) => {
            const { value, label, options, downDivider } = item;

            if (item.value === "date" || item.value === "personalized") {
              return null;
            }
            return !(options && hasCheckbox && isMultiple) ? (
              <SelectItem
                textValue={value}
                className={`${downDivider ? itemDivider : ""} ${
                  hasCheckbox || hasRadio ? checkOrRadio : ""
                } ${confirmSelection ? scrollableContent : ""} opacity-100`}
                key={value}
                value={value}
                aria-label={label}
              >
                <div className={optionsContainer}>
                  <p
                    style={
                      disabledOptions?.includes(value) ? { opacity: 0.5 } : {}
                    }
                  >
                    {getCustomSelectItem ? (
                      getCustomSelectItem(item)
                    ) : (
                      <ItemContent value={value} label={label} />
                    )}
                  </p>
                  {optionsTooltip?.[value] && (
                    <Tooltip
                      content={optionsTooltip[value]}
                      placement="top"
                      showArrow={true}
                      color="primary"
                      className={tooltipContainer}
                    >
                      <img src={InfoIcon} className={tooltipIcon} />
                    </Tooltip>
                  )}
                </div>
              </SelectItem>
            ) : (
              <SelectSection
                key={value}
                onClick={() => handleSectionClick(options, value)}
                className={hasCheckbox ? section : ""}
                title={
                  hasCheckbox
                    ? ((
                        <Checkbox
                          onClick={() => handleSectionClick(options, value)}
                          value={value}
                          isSelected={Array.from(values).includes(value)}
                        >
                          {label}
                        </Checkbox>
                      ) as any)
                    : label
                }
              >
                {options.map(({ value: optionValue, label: optionLabel }) => (
                  <SelectItem
                    textValue={optionValue}
                    key={optionValue}
                    value={optionValue}
                    aria-label={optionLabel}
                    className={confirmSelection ? scrollableContent : ""}
                  >
                    {getCustomSelectItem ? (
                      getCustomSelectItem(item)
                    ) : (
                      <ItemContent value={optionValue} label={optionLabel} />
                    )}
                  </SelectItem>
                ))}
              </SelectSection>
            );
          })}

        {confirmSelection &&
          ((
            <SelectItem
              textValue="actions"
              key="actions"
              value="actions"
              className={confirmActions}
            >
              <div>
                <Button
                  onPress={handleResetChanges}
                  variant="secondary"
                  text="Restablecer"
                />
                <Button
                  onPress={() => {
                    if (!disabledAplyChanges) {
                      handleConfirmChanges();
                    }
                  }}
                  text="Aplicar"
                  disabled={disabledAplyChanges}
                />
              </div>
            </SelectItem>
          ) as any)}

        {/* Append options which open date picker in different states if select has date picker */}
        {hasDatePicker &&
          !isDatePickerOpen &&
          !hideMonthlyOption &&
          ((
            <SelectItem textValue="actions" key="pick-month" value="pick-month">
              <span
                onClick={() => openDatePicker("MONTH")}
                className={datePickerOption}
              >
                Mensual <img src={ChevronIcon} />
              </span>
            </SelectItem>
          ) as any)}

        {hasDatePicker &&
          !isDatePickerOpen &&
          ((
            <SelectItem textValue="actions" key="pick-day" value="pick-day">
              <span
                onClick={() => openDatePicker("DAY")}
                className={datePickerOption}
              >
                Otro período <img src={ChevronIcon} />
              </span>
            </SelectItem>
          ) as any)}
        {/* Show actual date picker if open, no other options will be shown when open */}
        {isDatePickerOpen &&
          ((
            <SelectItem
              textValue="actions"
              key="date-picker"
              value="date-picker"
              className={datePicker}
            >
              <DatePicker
                onApply={onDatePickerApply}
                onDelete={() => {
                  prevValuesBeforePicker.current = null;
                  prevRangeBeforePicker.current = undefined;
                  if (onReset) {
                    setIsDatePickerOpen(false);
                    setDatePickerRange(undefined);
                    if (defaultSelectedKeys) {
                      setValues(defaultSelectedKeys);
                    }
                    onReset();
                    setIsOpen(false);
                  } else {
                    onDatePickerDelete();
                  }
                }}
                onBack={() => {
                  if (prevValuesBeforePicker.current !== null) {
                    setValues(prevValuesBeforePicker.current);
                    setDatePickerRange(prevRangeBeforePicker.current);
                    prevValuesBeforePicker.current = null;
                    prevRangeBeforePicker.current = undefined;
                  }
                  setIsDatePickerOpen(false);
                }}
                onChange={(range) => {
                  setDatePickerRange(range);
                  if (hideMonthlyOption) setValues("personalized");
                }}
                min={minDatePickerDate}
                max={maxDatePickerDate}
                defaultRange={datePickerRange}
                minDays={minDays}
                defaultCalendarVariant={datePickerInitialVariant}
                resetActionLabel
                calendarPreFooter={calendarPreFooter}
                calendarLimitMonthsAvailable={calendarLimitMonthsAvailable}
                footerClassName={footerClassName}
              />
            </SelectItem>
          ) as any)}
      </NextUiSelect>

      {isMultiple && showExternalBox && values && (
        <div className={externalBox}>
          {Array.isArray(values) &&
            values.map((value) => {
              const item = items.find((item) => item.value === value);
              return (
                value !== "" && (
                  <div className={externalItem} key={value}>
                    <span>{item?.label}</span>
                    <img
                      src={CloseIcon}
                      onClick={() => {
                        setValues(values.filter((val) => val !== value));
                        onChange(values.filter((val) => val !== value));
                      }}
                    />
                  </div>
                )
              );
            })}
        </div>
      )}
    </div>
  );
};

export default InputSelect;
